import { Box, styled } from '@mui/material';
import { useIsExploreUser } from 'hooks/client';
import { INDICATOR_CARD_WIDTH } from 'modules/decision/constants';
import { type LibraryIndicatorT } from 'modules/decision/types/indicators';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SeeMoreButton from 'ui/AdaptiveChipList/SeeMoreButton';
import Flex from 'ui/Flex/Flex';

import {
    getIndicatorTags,
    getPreviewImageForVizType,
    isCityIndicator,
} from '../helpers';
import IndicatorActions from './IndicatorActions';
import IndicatorCardPaper from './IndicatorCardPaper';
import IndicatorHeader from './IndicatorHeader';
import IndicatorTag from './IndicatorTag';

const StyIndicatorCard = styled(IndicatorCardPaper)({
    gap: 0,
    height: '400px',
    maxWidth: 'none',
    width: `${INDICATOR_CARD_WIDTH}px`,
});

const StyPreviewImage = styled('img', {
    shouldForwardProp: (prop) => prop !== 'areFilterChipsOverflowing',
})<{
    areFilterChipsOverflowing: boolean;
}>(({ areFilterChipsOverflowing }) => ({
    objectFit: 'contain',
    height: areFilterChipsOverflowing ? 170 : 205,
}));

// This value corresponds to the height of two chips + a gap of 8px between them
const INDICATOR_TAG_SMALL_CONTAINER_MAX_HEIGHT = '56px';
// This value corresponds to the height of 8 chips + a gap of 8px between them
const INDICATOR_TAG_LARGE_CONTAINER_MAX_HEIGHT = '248px';

const StyIndicatorTagsContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'showMoreFilters',
})<{ showMoreFilters: boolean }>(({ showMoreFilters, theme }) => ({
    alignItems: 'center',
    display: 'flex',
    flexFlow: 'row wrap',
    justifyContent: 'flex-start',
    gap: theme.spacing(1),
    maxHeight: showMoreFilters
        ? INDICATOR_TAG_LARGE_CONTAINER_MAX_HEIGHT
        : INDICATOR_TAG_SMALL_CONTAINER_MAX_HEIGHT,
    overflowY: showMoreFilters ? 'auto' : 'hidden',
    width: '100%',
}));

type Props = {
    indicator: LibraryIndicatorT;
};

const IndicatorCardLibrary = ({ indicator }: Props): JSX.Element => {
    const { t } = useTranslation();

    const chipsContainerRef = useRef<HTMLDivElement>(null);
    const [showMoreChips, setShowMoreChips] = useState(false);
    const [areFilterChipsOverflowing, setAreFilterChipsOverflowing] =
        useState(false);

    const isExploreUser = useIsExploreUser();

    useEffect(() => {
        const chipsContainer = chipsContainerRef.current;
        if (chipsContainer) {
            setAreFilterChipsOverflowing(
                chipsContainer.scrollHeight > chipsContainer.clientHeight,
            );
        }
    }, []);

    const indicatorTags = getIndicatorTags(indicator, t);

    return (
        <StyIndicatorCard>
            <Flex direction="column" gap={1}>
                <IndicatorHeader
                    indicator={indicator}
                    isExploreUser={isExploreUser}
                />
                <Flex direction="column">
                    <StyIndicatorTagsContainer
                        showMoreFilters={showMoreChips}
                        ref={chipsContainerRef}
                    >
                        {indicatorTags.map((indicatorTag, index) => (
                            <IndicatorTag
                                key={`filter-chip-${indicatorTag.value}-${index}`}
                                indicatorTag={indicatorTag}
                            />
                        ))}
                    </StyIndicatorTagsContainer>
                    {areFilterChipsOverflowing && (
                        <SeeMoreButton
                            showMore={showMoreChips}
                            onClick={() => setShowMoreChips(!showMoreChips)}
                        />
                    )}
                </Flex>
            </Flex>
            {!showMoreChips && (
                <StyPreviewImage
                    alt="Indicator preview image"
                    areFilterChipsOverflowing={areFilterChipsOverflowing}
                    src={
                        isCityIndicator(indicator)
                            ? getPreviewImageForVizType(indicator.vizType)
                            : indicator.preview
                    }
                />
            )}
            <IndicatorActions
                indicator={indicator}
                isExploreUser={isExploreUser}
            />
        </StyIndicatorCard>
    );
};

export default IndicatorCardLibrary;
